import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  daysSince,
  formatWeight,
  isPureBodyweightPr,
  type RecentPr,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { PagerNav } from "../primitives/PagerNav";
import { formatRelativeAge } from "../formatRelativeAge";
import { usePagerScroll } from "../primitives/usePagerScroll";
import { weightUnitLabels } from "../weightUnitLabels";

// The Home PR gallery (HR-9b, Antenore 2026-07-04): ONE big gold badge per
// page, swiped like a photo gallery, with the shared PagerNav (arrows +
// tappable dots + counter) below — a whole badge is always on screen, never
// one cut mid-scroll, and the experience is the SAME on native and web (the
// arrows/dots are the mouse path; no Platform fork). Lives inside Home's
// "Badges" group — HomeView owns the group header and the News sibling box.
// One badge per exercise (freshest PR within the recency window), trophy +
// name + value + age. Hidden entirely when there are no recent PRs. Names
// resolve by slug from the live catalog via the host-provided resolveName;
// tapping a badge opens Progress.
type Props = {
  prs: RecentPr[];
  resolveName: (slug: string, snapshot?: string) => string;
  theme: UiTheme;
  units: UnitsPreference;
  locale: string;
  onPress: () => void;
};

export function RecentPrsStrip({
  prs,
  resolveName,
  theme,
  units,
  locale,
  onPress,
}: Props) {
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);
  const pager = usePagerScroll(prs.length);
  if (prs.length === 0) return null;
  const multi = prs.length > 1;

  return (
    <View style={styles.wrap}>
      <View style={styles.viewport} onLayout={pager.onLayout}>
        {pager.width > 0 ? (
          <ScrollView
            ref={pager.scrollRef}
            horizontal
            pagingEnabled={multi}
            scrollEnabled={multi}
            showsHorizontalScrollIndicator={false}
            onScroll={pager.onScroll}
            scrollEventThrottle={16}
            directionalLockEnabled
          >
            {prs.map((pr, i) => {
              // CN-day-math boy-scout: one of the five hand-rolled copies of
              // this sum, collapsed now that shared owns `daysSince`.
              const daysAgo = daysSince(Date.parse(pr.setAt), Date.now());
              return (
                // Off-screen pages leave the a11y tree: a screen reader
                // navigates the gallery via PagerNav, not by wandering onto
                // invisible pages (classic horizontal-pager trap).
                <View
                  key={`${pr.exerciseId}#${pr.repMax}`}
                  style={{ width: pager.width }}
                  importantForAccessibility={
                    i === pager.index ? "auto" : "no-hide-descendants"
                  }
                  accessibilityElementsHidden={i !== pager.index}
                >
                  <Pressable
                    accessibilityRole="button"
                    onPress={onPress}
                    style={({ pressed }) => [
                      styles.badge,
                      pressed && styles.badgePressed,
                    ]}
                  >
                    {/* Monochrome trophy tinted with the gold status token —
                        NOT the 🏆 platform emoji (multicolour clashed with
                        the one-accent system and added screen-reader noise;
                        the icon is decorative, the texts carry the meaning).
                        Gold is a STATUS colour: nothing accent-coloured sits
                        inside the badge (DESIGN.md). */}
                    {/* "PR" micro-cap — the set-row PR badge idiom: names the
                        thing for sighted users (the group header only says
                        "Badges") and is the first text a screen reader hears
                        inside the badge. Same string in all 5 locales. */}
                    <Text style={styles.badgeKicker}>PR</Text>
                    <Icon
                      name="trophy"
                      size={theme.typography.sizes["2xl"]}
                      color={theme.palette.pr}
                    />
                    <Text style={styles.badgeName} numberOfLines={2}>
                      {resolveName(pr.exerciseId)}
                    </Text>
                    {/* Pure-bodyweight rep PRs show the reps-BW idiom
                        (same voice as the logger history rows) instead
                        of "0 kg × 20". */}
                    <Text style={styles.badgeValue}>
                      {isPureBodyweightPr(pr.weightKg)
                        ? t("mobile.home.recentPrValueBodyweight", {
                            defaultValue: "{{reps}} reps BW",
                            reps: pr.repMax,
                          })
                        : t("mobile.home.recentPrValue", {
                            defaultValue: "{{weight}} × {{reps}}",
                            weight: formatWeight(pr.weightKg, units, locale, unitLabels),
                            reps: pr.repMax,
                          })}
                    </Text>
                    <Text style={styles.badgeAge}>
                      {/* CN-relative-age — the shared formatter, so an old PR
                          reads "8 sett." instead of a number to divide. */}
                      {formatRelativeAge(t, daysAgo)}
                    </Text>
                  </Pressable>
                </View>
              );
            })}
          </ScrollView>
        ) : null}
      </View>
      <PagerNav
        theme={theme}
        index={pager.index}
        count={prs.length}
        onGoTo={pager.goTo}
        prevLabel={t("mobile.home.prPrev", {
          defaultValue: "Previous record",
        })}
        nextLabel={t("mobile.home.prNext", { defaultValue: "Next record" })}
        positionLabel={t("mobile.home.prPosition", {
          n: pager.index + 1,
          total: prs.length,
          defaultValue: "Record {{n}} of {{total}}",
        })}
        dotLabel={(i) =>
          t("mobile.home.prPosition", {
            n: i + 1,
            total: prs.length,
            defaultValue: "Record {{n}} of {{total}}",
          })
        }
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    wrap: { marginBottom: s.s4, gap: s.s2 },
    // The viewport carries the s5 gutter so the badge aligns with the
    // sibling Home cards; its measured width is the page width.
    viewport: { marginHorizontal: s.s5 },
    // The "quadratone": one generous centred gold card per page. Hierarchy
    // is typography (big mono value), colour stays the single gold status.
    badge: {
      alignItems: "center",
      justifyContent: "center",
      gap: s.s2,
      minHeight: 180,
      paddingVertical: s.s5,
      paddingHorizontal: s.s5,
      borderRadius: r.r4,
      backgroundColor: p.prFill,
      borderColor: p.pr,
      borderWidth: 1,
    },
    badgePressed: { opacity: 0.7 },
    // Micro-cap kicker, the set-row "PR" badge voice (2xs uppercase tracked).
    badgeKicker: {
      color: p.ink,
      fontSize: t.sizes["2xs"],
      fontWeight: "700",
      letterSpacing: 1,
    },
    badgeName: {
      color: p.ink,
      fontSize: t.sizes.md,
      fontWeight: "700",
      textAlign: "center",
    },
    // The number is the product: xl mono, the badge has the room (a11y
    // auditor HR-9b — reduced-vision headroom in a 180pt card).
    badgeValue: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "700",
      fontFamily: t.fonts.mono,
    },
    // Full ink, hierarchy via the smaller size — ink2 on prFill is under the
    // 4.5 contrast floor on catppuccin-light (the default theme).
    badgeAge: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: "500",
    },
  });
}
